import React, { useState, useEffect, useRef } from 'react';

import { PagoCliente } from '../../models/PagoCliente';
import theme, { cn } from '../../styles/theme';

const PagoClienteForm = ({ 
  pagoc, 
  onSubmit, 
  onCancel, 
  isLoading, 
  sucursalId,
  sucursalNombre
}) => {
  const [formData, setFormData] = useState({
    Id_pagoc: '',   
    Descripcion: '',    
    Imagen: '',
    ImagenFile: null,
    Estado: 'ACTIVO',    
    Id_sucursal: sucursalId || ''
  });

  
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  

  useEffect(() => {
    if (pagoc) {
      const Instance = new PagoCliente(pagoc);
      
      setFormData({
        Id_pagoc: Instance.Id_pagoc,       
        Descripcion: Instance.Descripcion,        
        Imagen: Instance.Imagen,
        ImagenFile: Instance.ImagenFile,
        Estado: Instance.Estado,        
        Id_sucursal: Instance.Id_sucursal || sucursalId       
      });
      
      if (Instance.Imagen) {
        const imageUrl = Instance.getImageUrl();
        setImagePreview(imageUrl);
      }
    } else {
      setFormData({
        Id_pagoc: '',
        Descripcion: '',
        Imagen: '',
        ImagenFile: null,
        Estado: 'ACTIVO',
        Id_sucursal: sucursalId || ''
      });
      setImagePreview(null);
    }
  }, [pagoc, sucursalId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'Id_categoria' && value !== '') {
      processedValue = parseInt(value, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleFileChange = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        imagen: 'Por favor selecciona un archivo de imagen válido'
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        imagen: 'La imagen no debe superar los 5MB'
      }));
      return;
    }

    try {
      const tempPagoc = new PagoCliente(formData);
      await tempPagoc.setImageFromFile(file);
      
      setFormData(prev => ({
        ...prev,
        Imagen: tempPagoc.Imagen,
        ImagenFile: tempPagoc.ImagenFile
      }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setErrors(prev => ({
        ...prev,
        imagen: null
      }));
      
    } catch (error) {
      console.error('Error procesando imagen:', error);
      setErrors(prev => ({
        ...prev,
        imagen: 'Error al procesar la imagen'
      }));
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileChange(files[0]);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      Imagen: '',
      ImagenFile: null
    }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
       
    if (!formData.Descripcion.trim()) {
      newErrors.Descripcion = 'La descripción es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const pagocToSubmit = new PagoCliente({
        ...formData,
        Id_sucursal: sucursalId
      });
      
      onSubmit(pagocToSubmit);
    }
  };

 

  return (
    <div className={cn(theme.classes.card.base, theme.shadows.lg, "p-8 max-w-4xl mx-auto")}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className={cn(theme.classes.iconBox.primary, "p-3")}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7" />
          </svg>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {pagoc ? 'Editar Pago' : 'Crear Nuevo Pago'}
          </h3>
          <p className="text-gray-600 mt-1">
            Sucursal: <span className="font-semibold text-gray-900">{sucursalNombre}</span>
          </p>
          <p className="text-sm text-gray-500">
            {pagoc ? 'Modifica la información del pago seleccionado' : 'Completa los datos del nuevo pago'}
          </p>
        </div>
      </div>
      
      {pagoc && (
        <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-gray-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-gray-700 font-medium">Información de Debug</p>
              <p className="text-sm text-gray-600 mt-1">
                Editando pago ID: <span className="font-mono font-semibold">{formData.Id_pagoc}</span> | 
                
              </p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sección de imagen */}
        <div className="space-y-2">
          <label className={theme.classes.form.label}>
            Imagen del Pago
          </label>
          
          <div
            className={cn(
              "relative border-2 border-dashed rounded-lg p-6",
              theme.transitions.default,
              isDragging 
                ? 'border-gray-400 bg-gray-50' 
                : imagePreview 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {imagePreview ? (
              <div className="text-center">
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Vista previa"
                    className={cn("w-32 h-32 object-cover rounded-lg border border-gray-200", theme.shadows.sm)}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className={cn(
                      "absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600",
                      theme.transitions.default,
                      theme.shadows.lg
                    )}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-green-600 mt-3 font-medium">Imagen cargada correctamente</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-gray-600 hover:text-gray-900 underline mt-1"
                >
                  Cambiar imagen
                </button>
              </div>
            ) : (
              <div className="text-center">
                <svg
                  className={cn("mx-auto h-12 w-12", isDragging ? 'text-gray-400' : 'text-gray-400')}
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="mt-4">
                  <p className={cn("text-sm", isDragging ? 'text-gray-600' : 'text-gray-600')}>
                    <span className="font-medium">
                      {isDragging ? 'Suelta la imagen aquí' : 'Arrastra una imagen aquí, o'}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 text-sm text-gray-700 hover:text-gray-900 underline"
                  >
                    busca en tu computadora
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  PNG, JPG, GIF hasta 5MB
                </p>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
          
          {errors.imagen && (
            <p className="text-red-600 text-sm font-medium flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.imagen}
            </p>
          )}
        </div>

        {/* Primera fila - Nombre y Precio */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          
        </div>

        {/* Segunda fila - Categoría y Estado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          

          {/* <div className="space-y-2">
            <label className={theme.classes.form.label}>
              Estado
            </label>
            <div className="relative">
              <select
                name="Estado"
                value={formData.Estado}
                onChange={handleChange}
                className={theme.classes.form.select}
              >
                <option value="ACTIVO">ACTIVO</option>
                <option value="INACTIVO">INACTIVO</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn("w-3 h-3 rounded-full", formData.Estado === 'ACTIVO' ? 'bg-green-500' : 'bg-red-500')}></div>
              <span className={cn("text-sm font-medium", formData.Estado === 'ACTIVO' ? 'text-green-700' : 'text-red-700')}>
                {formData.Estado === 'ACTIVO' ? 'Producto activo' : 'Producto inactivo'}
              </span>
            </div>
          </div> */}
        </div>

        {/* Campo Descripción */}
        <div className="space-y-2">
          <label className={theme.classes.form.label}>
            Descripción
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="relative">
            <textarea
              rows={4}
              name="Descripcion"
              value={formData.Descripcion}
              onChange={handleChange}
              className={cn(
                theme.classes.form.input,
                "resize-none",
                errors.Descripcion && theme.classes.form.inputError
              )}
              placeholder="Detalla la información del producto: ingredientes, características especiales, etc..."
            />
            <div className="absolute top-3 right-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </div>
          </div>
          {errors.Descripcion && (
            <p className="text-red-600 text-sm font-medium flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.Descripcion}
            </p>
          )}
        </div>

        {/* Información de la sucursal */}
        {sucursalId && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2 bg-gray-100 rounded-lg")}>
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Sucursal asignada: <span className="font-semibold">{sucursalNombre}</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  ID de sucursal: {sucursalId}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={cn(
              theme.classes.button.secondary,
              "px-6 py-3 font-semibold rounded-lg",
              theme.transitions.default,
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              theme.classes.button.primary,
              "px-8 py-3 font-semibold rounded-lg transform hover:-translate-y-0.5",
              theme.transitions.default,
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {pagoc ? 'Actualizar Pago' : 'Crear Pago'}
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PagoClienteForm;